// HighRiskCountries.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../DesignSystem/Sidebar";
import Select from "react-select";
import countryList from "react-select-country-list";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../backend/firebaseConfig";
import "../../../css/Compliance/HighRiskCountries/highRiskCountries.css";
import ButtonGreen from "../../DesignSystem/Buttons/ButtonGreen";
import { auth } from "../../../backend/firebaseConfig";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Check2AnnouncementAdmin from "../AMLAnnouncements/Check2AnnouncementAdmin";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";


const HighRiskCountries = () => {
  const [selectedCountryCodes, setSelectedCountryCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // ← JS-safe
  const { userPermissions } = useUserPermissions();
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const navigate = useNavigate();
  // Firestore location: collection "Compliance", doc "High Risk And Non Cooperative Countries"
  const docRef = useMemo(
    () => doc(db, "Compliance", "High Risk And Non Cooperative Countries"),
    []
  );

  // Options from react-select-country-list: [{ value: "GB", label: "United Kingdom" }, ...]
  const countryOptions = useMemo(() => countryList().getData(), []);

  // Map stored codes -> react-select value
  const selectedOptions = useMemo(
    () => countryOptions.filter((o) => selectedCountryCodes.includes(o.value)),
    [countryOptions, selectedCountryCodes]
  );

  // Load existing list from Firestore on mount
  useEffect(() => {
    const fetchHighRiskCountries = async () => {
      try {
        const snap = await getDoc(docRef);

        if (snap.exists()) {
          const data = snap.data();
          // Firestore field is `list`
          setSelectedCountryCodes(data.list || []);
          const updatedBy = data.updatedBy || null;
          const updatedAt = data.updatedAt || null;
          setLastUpdatedBy(updatedBy);
          setLastUpdatedAt(updatedAt);
        } else {
          setSelectedCountryCodes([]);
        }
      } catch (err) {
        console.error(
          "[HighRiskCountries] Failed to load high-risk countries:",
          err
        );
      } finally {
        setLoading(false);
      }
    };

    fetchHighRiskCountries();
  }, [docRef]);

  // Handle dropdown change
  const handleChange = (newValue) => {
    const codes = (newValue || []).map((opt) => opt.value);
    setSelectedCountryCodes(codes);
    setSaveStatus(null);
  };

  // Save list to Firestore
  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);

    try {
      await setDoc(
        docRef,
        {
          list: selectedCountryCodes, // ← matches Firestore field
          updatedAt: new Date().toISOString(),
          updatedBy:
            auth.currentUser?.displayName ||
            auth.currentUser?.email ||
            "Unknown User",
        },
        { merge: true }
      );
      setSaveStatus("success");
    } catch (err) {
      console.error(
        "[HighRiskCountries] Failed to save high-risk countries:",
        err
      );
      setSaveStatus("error");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!userPermissions.includes("Compliance")) {
      navigate("/");
    }
  }, [userPermissions]);

  return (
    <div className="Main-Page">
      <Helmet>
        <title>High Risk & Non-Cooperative Countries</title>
        <meta
          name="description"
          content="Manage high-risk & non-cooperative countries for AML checks."
        />
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <Breadcrumb
          items={[
            {
              label: "Compliance",
              to: "/Department/Compliance",
            },
            { label: "High Risk & Non-Cooperative Countries" },
          ]}
        />
        <div>
          <h2>High Risk &amp; Non-Cooperative Countries</h2>
        </div>

        {loading ? (
          <p>Loading high-risk country settings…</p>
        ) : (
          <div className="High-Risk-Countries-Card">
            {userPermissions.includes("Administrator") && (
              <div>
                Last updated by <strong>{lastUpdatedBy}</strong> on{"  "}
                <strong>
                  {dayjs(lastUpdatedAt).format("MMMM D, YYYY h:mm A")}
                </strong>
                .
              </div>
            )}
            <div className="Content">
              Select the countries that should be treated as{" "}
              <strong>high risk or non-cooperative</strong>.
            </div>

            <Select
              options={countryOptions}
              value={selectedOptions}
              onChange={handleChange}
              isMulti
              placeholder="Select high-risk countries…"
              classNamePrefix="hrc-select"
              styles={{
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: "#e9ebf8", // light blue
                  borderRadius: "6px",
                  border: "1px solid #b4b8c5",
                  padding: "3px 8px",
                  marginRight: "5px",
                  marginBottom: "5px",
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: "#081e56",
                  fontWeight: "500",
                  fontFamily: "'Inter', sans-serif",
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: "#081e56",
                  ":hover": {
                    backgroundColor: "#cc0202",
                    color: "white",
                  },
                }),
              }}
            />

            <ButtonGreen
              buttonText={isSaving ? "Saving…" : "Save changes"}
              onClick={handleSave}
              disabled={isSaving}
            ></ButtonGreen>

            {saveStatus === "success" && (
              <span style={{ color: "green", fontSize: "0.9rem" }}>
                Saved successfully.
              </span>
            )}
            {saveStatus === "error" && (
              <span style={{ color: "red", fontSize: "0.9rem" }}>
                Something went wrong while saving.
              </span>
            )}

            {selectedCountryCodes.length > 0 && (
              <div className="High-Risk-Countries-Grid">
                <div
                  className="Content-Bold-Underline "
                  style={{ gridColumn: "1 / -1" }}
                >
                  Currently configured High Risk & Non-Cooperative Countries:
                </div>

                {selectedOptions.map((opt, idx) => (
                  <div key={opt.value} className="Content">
                    {opt.label} ({opt.value})
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      <Check2AnnouncementAdmin />
      </div>
    </div>
  );
};

export default HighRiskCountries;
